// ---------------------------------------------------------------------------
// niceTowns — a curated registry of genuinely pleasant towns to route THROUGH.
//
// Morgan: "for drives, if there's an option to go through a much nicer town
// than another, they should opt for those routes — not White Plains, but
// Greenwich and close-by towns are much better and should be counted so."
//
// This is a *positive* layer: a route that passes through (or right by) a nice
// town earns a small, capped bonus, nudging the scorer toward the charming
// way when the time cost is comparable. It is NOT a penalty list — a town's
// absence here simply means "no bonus", never a demerit. Extend it the same
// way the curated walk cities grow: add { name, point:[lng,lat], score }.
//
// score is a rough 1–3 charm weight (3 = standout). Keep points at the town
// center; matching uses a generous radius so a route skirting the edge of a
// nice town still counts.
// ---------------------------------------------------------------------------
import { nearestDistance, type LngLat } from "@/utils/polyline";

export interface NiceTown {
  name: string;
  point: LngLat; // [lng, lat]
  score: number; // 1..3 charm weight
}

// Starter set centered on the NY / CT / lower-Hudson metro (Morgan's example
// corridor), plus a handful of standouts. This is meant to grow.
export const NICE_TOWNS: NiceTown[] = [
  // Fairfield County, CT
  { name: "Greenwich", point: [-73.6284, 41.0262], score: 3 },
  { name: "New Canaan", point: [-73.4954, 41.1468], score: 2 },
  { name: "Westport", point: [-73.3579, 41.1415], score: 2 },
  { name: "Ridgefield", point: [-73.4982, 41.2815], score: 2 },
  { name: "Darien", point: [-73.4690, 41.0787], score: 2 },
  // Westchester / lower Hudson, NY
  { name: "Bedford", point: [-73.6435, 41.2048], score: 3 },
  { name: "Pound Ridge", point: [-73.5751, 41.2076], score: 2 },
  { name: "Katonah", point: [-73.6857, 41.2604], score: 2 },
  { name: "Chappaqua", point: [-73.7696, 41.1593], score: 2 },
  { name: "Rye", point: [-73.6837, 40.9807], score: 2 },
  { name: "Larchmont", point: [-73.7518, 40.9279], score: 2 },
  { name: "Scarsdale", point: [-73.7846, 40.9884], score: 1 },
  { name: "Tarrytown", point: [-73.8587, 41.0762], score: 2 },
  { name: "Sleepy Hollow", point: [-73.8588, 41.0856], score: 2 },
  { name: "Irvington", point: [-73.8682, 41.0398], score: 2 },
  { name: "Cold Spring", point: [-73.9543, 41.4203], score: 3 },
  { name: "Nyack", point: [-73.9168, 41.0909], score: 2 },
  // Hudson Valley, NY
  { name: "Beacon", point: [-73.9696, 41.5048], score: 2 },
  { name: "Rhinebeck", point: [-73.9123, 41.9270], score: 3 },
  { name: "Hudson", point: [-73.7907, 42.2528], score: 2 },
  { name: "Woodstock", point: [-74.1182, 42.0409], score: 2 },
  { name: "Millbrook", point: [-73.6929, 41.7848], score: 2 },
  // Litchfield County, CT
  { name: "Litchfield", point: [-73.1887, 41.7470], score: 2 },
  { name: "Kent", point: [-73.4774, 41.7242], score: 2 },
  { name: "Salisbury", point: [-73.4226, 41.9831], score: 2 },
  // Long Island / Hamptons, NY
  { name: "Sag Harbor", point: [-72.2926, 40.9981], score: 2 },
  { name: "East Hampton", point: [-72.1852, 40.9634], score: 2 },
  // New England standouts
  { name: "Stowe", point: [-72.6874, 44.4654], score: 3 },
  { name: "Woodstock VT", point: [-72.5187, 43.6242], score: 3 },
  { name: "Camden", point: [-69.0648, 44.2098], score: 3 },
  { name: "Kennebunkport", point: [-70.4767, 43.3617], score: 2 },
  // Bay Area / Northern California
  { name: "Sausalito", point: [-122.4853, 37.8591], score: 3 },
  { name: "Mill Valley", point: [-122.5450, 37.9061], score: 2 },
  { name: "Tiburon", point: [-122.4566, 37.8736], score: 2 },
  { name: "Carmel-by-the-Sea", point: [-121.9233, 36.5552], score: 3 },
  { name: "Sonoma", point: [-122.4580, 38.2919], score: 2 },
  { name: "Healdsburg", point: [-122.8694, 38.6102], score: 2 },
];

/**
 * Nice towns this route passes through or close to, nearest-first.
 * `maxKm` is generous — a route within a few km of a town center is
 * effectively "going through" it for scoring purposes.
 */
export function niceTownsAlongRoute(
  coords: LngLat[],
  maxKm = 3.5,
): { name: string; score: number; km: number }[] {
  if (!coords || coords.length < 2) return [];
  const out: { name: string; score: number; km: number }[] = [];
  for (const t of NICE_TOWNS) {
    const m = nearestDistance(t.point, coords); // meters
    const km = m / 1000;
    if (km <= maxKm) out.push({ name: t.name, score: t.score, km });
  }
  return out.sort((a, b) => a.km - b.km);
}

/**
 * A small, capped scoring bonus for passing nice towns. Additive and bounded
 * so it nudges between comparable routes without overpowering the core
 * pleasantness signal. Closer passes and higher charm weigh more.
 */
export function niceTownBonus(coords: LngLat[], maxKm = 3.5, cap = 8): number {
  const towns = niceTownsAlongRoute(coords, maxKm);
  let bonus = 0;
  for (const t of towns) {
    // Full charm weight when going right through (≤1 km), tapering to ~0 at maxKm.
    const closeness = Math.max(0, 1 - t.km / maxKm);
    bonus += t.score * closeness;
  }
  return Math.min(cap, bonus);
}
